import { db } from "@/utils/firebase"
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore"
import { PORTFOLIOS_COLLECTION, BUSINESS_COLLECTION } from "../constants"
import {
  Member,
  MemberPermission,
} from "@/components/portfolio/settings/types/memberTypes"

/**
 * Update a member's permissions across portfolio, businesses, and invitations
 * @param portfolioId - The ID of the portfolio
 * @param memberId - The ID of the member in the portfolio
 * @param permissionId - The ID of the permission to update
 * @param checked - The new checked state of the permission
 */
export const updateMemberPermissions = async (
  portfolioId: string,
  memberId: string,
  permissionId: string,
  checked: boolean
): Promise<boolean> => {
  try {
    console.log(
      `Updating permission ${permissionId} to ${checked} for member ${memberId} in portfolio ${portfolioId}`
    )

    // Get the portfolio document
    const portfolioRef = doc(db, PORTFOLIOS_COLLECTION, portfolioId)
    const portfolioDoc = await getDoc(portfolioRef)

    if (!portfolioDoc.exists()) {
      console.error(`Portfolio ${portfolioId} not found`)
      return false
    }

    const portfolioData = portfolioDoc.data()
    const members = portfolioData.members || []

    // Find the member in the portfolio
    const member = members.find((m: Member) => m.userId === memberId)

    if (!member) {
      console.error(`Member ${memberId} not found in portfolio ${portfolioId}`)
      return false
    }

    // Create a batch for atomic updates
    const batch = writeBatch(db)

    // 1. Update the member's permissions in the portfolio
    const updatedMembers = members.map((m: Member) => {
      if (m.userId === memberId) {
        return {
          ...m,
          permissions: (m.permissions || []).map((p: MemberPermission) => {
            if (p.id === permissionId) {
              return { ...p, checked }
            }
            return p
          }),
        }
      }
      return m
    })

    batch.update(portfolioRef, { members: updatedMembers })

    // 2. Update the member's permissions in all businesses linked to this portfolio
    // Get all businesses for this portfolio from the businesses collection
    const businessesRef = collection(db, BUSINESS_COLLECTION)
    const businessesQuery = query(
      businessesRef,
      where("portfolioId", "==", portfolioId)
    )
    console.log(`Querying businesses with portfolioId=${portfolioId}`)
    const businessesSnapshot = await getDocs(businessesQuery)

    // Track the member's email for invitation updates
    const memberEmail = member.email

    for (const businessDoc of businessesSnapshot.docs) {
      const businessId = businessDoc.id
      const businessData = businessDoc.data()
      const businessMembers = businessData.members || []

      // Find the member in the business (by userId or email)
      const businessMember = businessMembers.find(
        (m: Member) =>
          m.userId === member.userId ||
          m.id === member.id ||
          m.email === member.email
      )
      console.log("Business member found", businessMember)

      if (businessMember) {
        console.log(`Updating member ${memberId} in business ${businessId}`)

        // Update the member's permissions in the business
        const updatedBusinessMembers = businessMembers.map((m: Member) => {
          if (
            m.userId === member.userId ||
            m.id === member.id ||
            m.email === member.email
          ) {
            return {
              ...m,
              permissions: (m.permissions || []).map((p: MemberPermission) => {
                if (p.id === permissionId) {
                  return { ...p, checked }
                }
                return p
              }),
            }
          }
          return m
        })

        const businessRef = doc(db, BUSINESS_COLLECTION, businessId)
        batch.update(businessRef, { members: updatedBusinessMembers })
      }
    }

    // 3. Update any pending invitations with the same email
    if (memberEmail) {
      const invitationsRef = collection(db, "invitations")
      const invitationsQuery = query(
        invitationsRef,
        where("email", "==", memberEmail),
        where("status", "==", "invited")
      )

      const invitationsSnapshot = await getDocs(invitationsQuery)

      for (const invitationDoc of invitationsSnapshot.docs) {
        const invitationId = invitationDoc.id
        const invitationData = invitationDoc.data()

        // Only update invitations for this portfolio or its businesses
        if (invitationData.portfolioId === portfolioId) {
          console.log(
            `Updating invitation ${invitationId} for email ${memberEmail}`
          )

          const updatedPermissions = (invitationData.permissions || []).map(
            (p: MemberPermission) => {
              if (p.id === permissionId) {
                return { ...p, checked }
              }
              return p
            }
          )
          console.log("UPDATED PERMISSION", updatedPermissions)

          const invitationRef = doc(db, "invitations", invitationId)
          batch.update(invitationRef, { permissions: updatedPermissions })
        }
      }
    }

    // Commit all the updates
    await batch.commit()

    console.log(
      `Successfully updated permissions for member ${memberId} across portfolio and businesses`
    )
    return true
  } catch (error) {
    console.error("Error updating member permissions:", error)
    return false
  }
}
